import { Flex, Text, Avatar, HStack, Stack } from '@chakra-ui/react';
import { motion } from 'framer-motion';
import SectionHeader from './SectionHeader';
import { Star } from 'lucide-react';

// Create motion components
const MotionFlex = motion(Flex);
const MotionStack = motion(Stack);

interface ReviewBoxProps {
	name: string;
    position: string;
    title: string;
    description: string;
    index: number;
}

export const REVIEWS_SUBTITLE = "Take a look at what past clients think. The results speak for themselves.";

export const ReviewData = [
	{
        name: "Ana S.",
        position: "2/23/2025",
        title: "His dedication is incredible.",
        description: "Facing a serious legal issue was one of the most terrifying experiences of my life. Joe treated me like a person, not just another case. He kept me informed, answered all my questions, and gave me the confidence to face the situation. His dedication is incredible, and we got the best possible outcome.",
	},
	{
        name: "Marcus T.",
        position: "3/25/2025",
        title: "I got my life back.",
        description: "I cannot recommend Joe enough! I was facing charges that could have completely derailed my life. From our first meeting, he was confident, professional, and compassionate. He walked me through every step of the process and fought hard for me. Thanks to him, the charges were dismissed, and I got my life back. Thank you for everything!",
	},
    {
        name: "Luke C.",
        position: "9/17/2024",
        title: "Saved me years of stress.",
        description: "Joe is a true professional who knows the law inside and out. He gave me honest advice, explained my options, and kept me calm through the entire process. He negotiated a plea deal that was far better than I expected, saving me years of stress. I would highly recommend him to anyone in need.",
	},
  ];
  

function ReviewBox({ name, position, title, description, index }: ReviewBoxProps) {
	return (
        <MotionStack
            maxW="320px"
            gap="4"
            padding={5}
            backgroundColor={"blue.400"}
            borderRadius={5}
            boxShadow="0px 4px 10px rgba(0, 0, 0, 0.1)"
			transition={"all 0.2s ease"}
            initial={{ opacity: 0, y: 50, scale: 0.95 }}
            whileInView={{ opacity: 1, y: 0, scale: 1 }}
            viewport={{ once: true, amount: 0.3 }}
            animate={{ 
                transition: { 
                    duration: 0.6, 
                    delay: index * 0.15, 
                    ease: "easeOut" 
                }
            }}
        >

            {/* Client Info */}
            <HStack gap="4">
                <Avatar
                    name={`${name}`}
                    variant={"subtle"}
					color={"white"}
                    backgroundColor={"yellow.100"}
                />
                <Stack textStyle="sm" gap="0">
                    <Text
						fontWeight="medium"
						color={"white.500"}
					>	{name}
					</Text>

                    <Text
						color={"white.500"}
					>
						{position}
					</Text>
                </Stack>
            </HStack>

            {/* Stars */}
            <MotionFlex
                flexDirection={"row"}
                width={"100%"}
                justifyContent={"center"}
                gap={2}
                initial={{ opacity: 0, scale: 0 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ 
                    duration: 0.4, 
                    delay: index * 0.15 + 0.3,
                }}
            >
                <Star color='gold' fill='gold' />
                <Star color='gold' fill='gold' />
                <Star color='gold' fill='gold' />
                <Star color='gold' fill='gold' />
                <Star color='gold' fill='gold' />
            </MotionFlex>

            {/* Title */}
            <Text
                fontWeight={"bold"}
                textAlign={"center"}
				color={"white.500"}
            >
                "{title}"
            </Text>

            {/* Description */}
            <Text
				color={"white.500"}
			>
                {description}
            </Text>
        </MotionStack>
	);
}

interface ReviewsProps {
	// Hide the section header when the page already has its own
	showHeader?: boolean;
}

export default function Reviews({ showHeader = true }: ReviewsProps) {

	return (
		<Flex
            id={"#reviews"}
			alignItems="center"
			justifyContent={"space-between"}
            backgroundColor={"blue.200"}
			width={"100%"}
            paddingTop={10}
			paddingBottom={10}
		>
			<Flex
				flexDirection={"column"}
				width={"90%"}
				margin={"auto"}
			>
				{/* Header */}
				{showHeader && (
					<MotionFlex
						initial={{ opacity: 0, y: 30 }}
						whileInView={{ opacity: 1, y: 0 }}
						viewport={{ once: true, amount: 0.5 }}
						transition={{ duration: 0.6, ease: "easeOut" }}
					>
						<SectionHeader
							title={"Reviews"}
							subtitle={REVIEWS_SUBTITLE}
						/>
					</MotionFlex>
				)}

				{/* Cases */}
				<Flex
					flexWrap="wrap"
					justifyContent="center"
					gap={6}
					mt={showHeader ? 8 : 0}
				>
					{ReviewData.map((reviewItem, index) => (
                        <Flex
                            key={index}
                            border={"1px solid transparent"}
                            transition={"all 0.2s ease"}
                            borderRadius={5}
                            _hover={{
                                borderColor: "yellow.100",
                                transform: "translateY(-2px)",
                                boxShadow: "lg",
                                cursor: "pointer",
                            }}
                        >
                            <ReviewBox {...reviewItem} index={index} />
                        </Flex>
					))}
				</Flex>
				
			</Flex>
		</Flex>
	);
};